import { Download, FileText, CheckCircle2, Target, Users, DollarSign, Clock, Package, Layers, Video } from 'lucide-react';
import { Button } from '../ui/button';
import { useState, useEffect, lazy, Suspense } from 'react';
import { getApiUrl, getApiHeaders } from '../../utils/apiConfig';

// Lazy load modal — only needed on button click
const VideoPlannerModal = lazy(() => import('../VideoPlannerModal').then(m => ({ default: m.VideoPlannerModal })));

const iconMap: { [key: string]: any } = {
  Download, FileText, Target, Users, DollarSign,
  CheckCircle2, Clock, Package, Layers, Video,
};

interface VideoPlannerItem {
  id: string;
  icon: string;
  text: string;
  color: string;
  order: number;
}

interface VideoPlannerSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  title: string;
  description: string;
  ctaText: string;
  cardTitle: string;
  items: VideoPlannerItem[];
  freeBadgeText: string;
  freeBadgeSubtext: string;
  visible: boolean;
  updatedAt: string;
}

// ============================================
// SKELETON
// ============================================

const VideoPlannerSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#0a0a1f] overflow-hidden">
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">

        {/* Left — text content */}
        <div className="space-y-6">
          {/* Badge */}
          <div className="h-8 w-32 bg-white/20 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          {/* Title */}
          <div className="space-y-3">
            <div className="h-9 bg-white/20 rounded w-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-9 bg-white/20 rounded w-3/4 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          {/* Description */}
          <div className="space-y-2">
            <div className="h-4 bg-white/20 rounded w-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-4 bg-white/20 rounded w-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-4 bg-white/20 rounded w-2/3 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          {/* CTA button */}
          <div className="h-12 w-52 bg-white/20 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>

        {/* Right — card */}
        <div className="relative">
          <div className="bg-white/10 rounded-3xl p-8 sm:p-10 border border-white/20 space-y-6">
            {/* Icon */}
            <div className="w-16 h-16 bg-white/20 rounded-2xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Card title */}
            <div className="h-6 w-40 bg-white/20 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* List items */}
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex-shrink-0 bg-white/20 rounded-lg relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                  <div className="h-4 bg-white/20 rounded flex-1 relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Floating badge */}
          <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>

      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function VideoPlanner() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sectionData, setSectionData] = useState<VideoPlannerSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideoPlannerSection();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchVideoPlannerSection();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchVideoPlannerSection = async () => {
    try {
      const timestamp = new Date().getTime();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(
        getApiUrl(`homepage/video-planner?t=${timestamp}`),
        {
          headers: getApiHeaders(),
          signal: controller.signal,
          cache: 'no-store',
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) return;

      const result = await response.json();
      if (result.success && result.data) {
        setSectionData(result.data);
      }
    } catch (error: any) {
      // silently fall through to defaults
    } finally {
      setLoading(false);
    }
  };

  const badgeText       = sectionData?.badgeText       || 'Free Resource';
  const badgeIcon       = sectionData?.badgeIcon       || 'Download';
  const title           = sectionData?.title           || 'Plan Your Next Manufacturing Video';
  const description     = sectionData?.description     || 'Access our Video Planning Template to define objectives, map key messages, and align stakeholders before production. A structured approach that ensures clarity, efficiency, and measurable outcomes.';
  const ctaText         = sectionData?.ctaText         || 'Download the Planner';
  const cardTitle       = sectionData?.cardTitle       || "What's Included:";
  const freeBadgeText   = sectionData?.freeBadgeText   || 'FREE';
  const freeBadgeSubtext= sectionData?.freeBadgeSubtext|| 'Template';
  const visible         = sectionData?.visible !== false;

  const defaultItems = [
    { id: '1', icon: 'Target',       text: "Project objectives & success metrics",    color: "from-[#D74444] to-[#e85e5e]", order: 1 },
    { id: '2', icon: 'Users',        text: "Target audience analysis framework",       color: "from-[#E87D3E] to-[#E8B84D]", order: 2 },
    { id: '3', icon: 'FileText',     text: "Key messaging & script outline",           color: "from-[#2F2E5C] to-[#4a487a]", order: 3 },
    { id: '4', icon: 'CheckCircle2', text: "Stakeholder approval checklist",           color: "from-[#D74444] to-[#e85e5e]", order: 4 },
    { id: '5', icon: 'DollarSign',   text: "Budget & timeline planning tools",         color: "from-[#E87D3E] to-[#E8B84D]", order: 5 },
  ];

  const items = sectionData?.items || defaultItems;
  const BadgeIcon = iconMap[badgeIcon] || Download;

  // Show skeleton while loading
  if (loading) {
    return <VideoPlannerSkeleton />;
  }

  // Don't render if section is hidden
  if (!visible) return null;

  return (
    <>
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#0a0a1f] text-white overflow-hidden">
        {/* Premium Multi-Layer Background System */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#0a0a1f]" />

        {/* Vibrant Premium Gradient Orbs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#D74444] via-[#e85e5e] to-[#ff6b6b] opacity-15 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}} />
          <div className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-tr from-[#E8B84D] via-[#E87D3E] to-[#ffa94d] opacity-15 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#4a487a] via-[#6b68a8] to-[#4a487a] opacity-10 rounded-full blur-3xl" />
        </div>

        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
        <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '50px 50px'}} />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-white via-transparent to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/30 shadow-lg">
                <BadgeIcon className="w-4 h-4 text-[#E8B84D]" />
                <span className="text-sm font-semibold bg-gradient-to-r from-[#E8B84D] to-[#E87D3E] bg-clip-text text-transparent">{badgeText}</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                {title}
              </h2>
              <p className="text-base sm:text-lg text-white/80 mb-8 leading-relaxed">
                {description}
              </p>

              <Button
                onClick={() => setIsModalOpen(true)}
                className="group relative overflow-hidden bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939] hover:from-[#c13939] hover:via-[#D74444] hover:to-[#e85e5e] text-white rounded-full px-8 h-12 gap-3 shadow-2xl shadow-[#D74444]/30 hover:scale-105 transition-all duration-300 font-bold"
                variant={null}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="relative z-10 flex items-center gap-3">
                  <Download className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  {ctaText}
                </span>
              </Button>
            </div>

            {/* Right Visual - Premium Card */}
            <div className="relative">
              <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 sm:p-10 border border-white/30 shadow-2xl overflow-hidden group hover:bg-white/[0.12] transition-all duration-300">
                <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
                <div className="absolute inset-0 bg-gradient-to-br from-[#E8B84D]/5 via-transparent to-[#D74444]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative z-10">
                  <div className="relative w-16 h-16 bg-gradient-to-br from-[#D74444] to-[#e85e5e] rounded-2xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#D74444] to-[#e85e5e] blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
                    <FileText className="w-8 h-8 text-white relative z-10" />
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold mb-6">{cardTitle}</h3>

                  <ul className="space-y-4">
                    {items.map((item) => {
                      const ItemIcon = iconMap[item.icon] || Target;
                      return (
                        <li key={item.id} className="flex items-start gap-3 group/item">
                          <div className={`flex-shrink-0 w-9 h-9 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center text-white shadow-lg group-hover/item:scale-110 transition-transform duration-300`}>
                            <ItemIcon className="w-5 h-5" />
                          </div>
                          <span className="text-sm sm:text-base text-white/90 leading-relaxed pt-1.5">{item.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-br from-[#E8B84D]/20 to-transparent rounded-full blur-2xl" />
              </div>

              {/* Floating Premium Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-[#E8B84D] via-[#E87D3E] to-[#d4a03d] rounded-2xl shadow-2xl p-4 text-white border-2 border-white/30">
                <div className="text-2xl font-bold">{freeBadgeText}</div>
                <div className="text-xs text-white/90">{freeBadgeSubtext}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal — lazy loaded, only mounted when opened */}
      <Suspense fallback={null}>
        <VideoPlannerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </Suspense>
    </>
  );
}